import { useEffect, useState } from "react";
import { Loader2, Bell, Save, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ITEMS = [
  ["email_notifications", "Email notifications", "Master switch — turn all OperaMind emails on or off."],
  ["appointment_confirmations", "Appointment confirmations", "Confirmations and cancellations for bookings."],
  ["new_lead_notifications", "New lead notifications", "When a new enquiry comes in from a call."],
  ["new_message_notifications", "New customer message notifications", "When a caller leaves a message."],
  ["missed_call_notifications", "Missed call notifications", "When a call is handled by your AI receptionist."],
  ["ai_summary_emails", "AI summary emails", "A concise AI summary after each call."],
  ["daily_summary", "Daily summary emails", "A roundup of the day's calls, bookings and messages."],
  ["weekly_report", "Weekly report", "A weekly performance report for your business."],
];

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/notifications/preferences").then((r) => setPrefs(r.data)).catch(() => {}); }, []);

  if (!prefs) return <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  const toggle = (k) => setPrefs({ ...prefs, [k]: !prefs[k] });
  const save = async () => {
    setSaving(true);
    try { await api.put("/notifications/preferences", prefs); toast.success("Notification preferences saved"); }
    catch (e) { toast.error("Could not save preferences"); }
    finally { setSaving(false); }
  };

  const master = prefs.email_notifications;

  return (
    <div data-testid="notifications-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Notification Settings</h1>
          <p className="text-slate-500 mt-1">Choose which professional emails OperaMind sends on your behalf.</p>
        </div>
        <Button data-testid="save-notifications-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
          <Mail className="h-4 w-4 text-slate-400" />
          <p className="text-sm text-slate-500">Emails are sent from <span className="font-semibold text-slate-700">notifications@operamind.ai</span> — no need to connect Gmail or Outlook.</p>
        </div>
        <div className="divide-y divide-slate-100">
          {ITEMS.map(([key, label, desc]) => {
            const disabled = key !== "email_notifications" && !master;
            return (
              <div key={key} className={`px-6 py-4 flex items-center justify-between gap-4 ${disabled ? "opacity-50" : ""}`}>
                <div className="flex items-start gap-3">
                  <Bell className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{label}</p>
                    <p className="text-xs text-slate-500">{desc}</p>
                  </div>
                </div>
                <Switch data-testid={`toggle-${key}`} checked={!!prefs[key]} disabled={disabled} onCheckedChange={() => toggle(key)} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
